import React, { useEffect, useRef, useState } from 'react';
import { useStripe, useElements, CardElement } from '@stripe/react-stripe-js';
import axiosInstance from '../../../../utils/axiosInstance';
import './MakeRecurringMembershipModal.css';

function MakeRecurringMembershipModal({ 
  isOpen, 
  membership, 
  onConfirm, 
  onCancel,
  user,
  cardDetails,
  setMembershipDetails
}) {
  const modalRef = useRef(null);
  const stripe = useStripe();
  const elements = useElements();
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showCardForm, setShowCardForm] = useState(false);
  const [useSavedCard, setUseSavedCard] = useState(true);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [localCardDetails, setLocalCardDetails] = useState(cardDetails);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onCancel();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onCancel]);

  useEffect(() => {
    if (isOpen && modalRef.current) {
      modalRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    setLocalCardDetails(cardDetails);
  }, [cardDetails]);

  useEffect(() => {
    if (isOpen) {
      setShowCardForm(false);
      setUseSavedCard(true);
      setErrorMessage('');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onCancel();
    }
  };

  const handleContentClick = (e) => {
    e.stopPropagation();
  };

  const handleSaveCard = async (e) => {
    e.preventDefault();
    setErrorMessage('');

    if (!stripe || !elements) {
      setErrorMessage('Stripe has not loaded yet. Please wait...');
      return;
    }

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      setErrorMessage('Payment element not found.');
      return;
    }

    try {
      setIsSaving(true);
      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
      });

      if (error) {
        setErrorMessage(error.message);
        setIsSaving(false);
        return;
      }

      const token = localStorage.getItem('token');
      const response = await axiosInstance.post(
        '/users/add-card',  
        { paymentMethodId: paymentMethod.id },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Update the user's card details in the parent component
      if (response.data.cardDetails) {
        user.stripeCardId = paymentMethod.id;
      }

      // Now make the membership recurring
      await handleMakeRecurring(paymentMethod.id);
    } catch (err) {
      console.error('Failed to save card:', err);
      setErrorMessage('Failed to save card. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleMakeRecurring = async (paymentMethodId = null) => {
    try {
      // Validate payment method selection
      if (!useSavedCard && !paymentMethodId) {
        setErrorMessage('Please either use your saved card or add a new payment method.');
        return;
      }

      setIsSaving(true);
      setErrorMessage('');

      const token = localStorage.getItem('token');
      const response = await axiosInstance.post(
        `/membership/${membership._id}/makeRecurring`,
        { paymentMethodId },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // If we have a client secret, we need to confirm the payment
      if (response.data.subscription?.latest_invoice?.payment_intent?.client_secret) {
        const { error: confirmError } = await stripe.confirmCardPayment(
          response.data.subscription.latest_invoice.payment_intent.client_secret,
          {
            payment_method: paymentMethodId || localCardDetails.id,
          }
        );

        if (confirmError) {
          throw new Error(confirmError.message);
        }
      }

      // Store the updated membership data for later use
      const updatedMembership = response.data.membership || {
        ...membership,
        isRecurring: true,
        nextPaymentDate: response.data.nextPaymentDate
      };

      setShowCardForm(false);
      setShowSuccessModal(true);
      
      // Store the updated membership in a ref to use when closing the success modal
      modalRef.current.updatedMembership = updatedMembership;
    } catch (err) {
      console.error('Error making membership recurring:', err);
      if (err.response?.data?.error === 'User missing Stripe customer ID') {
        setErrorMessage('Please add a payment method first.');
      } else {
        setErrorMessage(err.response?.data?.error || err.message || 'Failed to make membership recurring. Please try again.');
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleSuccessModalClose = () => {
    setShowSuccessModal(false);
    // Call onConfirm with the stored updated membership data
    if (modalRef.current?.updatedMembership) {
      onConfirm(modalRef.current.updatedMembership);
      modalRef.current.updatedMembership = null;
    }
    onCancel();
  };

  const calculateNextPaymentDate = () => {
    if (membership?.endDate) {
      return new Date(membership.endDate).toLocaleDateString();
    }
    const now = new Date();
    now.setDate(now.getDate() + 30);
    return now.toLocaleDateString();
  };

  return (
    <>
      {isOpen && (
        <div 
          className="overlay" 
          onClick={handleOverlayClick}
          role="dialog"
          aria-modal="true"
          aria-labelledby="modal-title"
        >
          <div 
            ref={modalRef}
            className="makeRecurringMembershipModal" 
            onClick={handleContentClick}
            tabIndex="-1"
          >
            <button 
              className="makeRecurringMembershipModalCloseButton" 
              onClick={onCancel}
              aria-label="Close modal"
            >
              ×
            </button>
            
            <h2 id="modal-title">Make This Membership Recurring?</h2>
            
            {!showCardForm ? (
              <>
                <div className="recurring-info-section">
                  <p>
                    Making this membership recurring will ensure uninterrupted access to the dog park.
                    Your membership will automatically renew starting from the end of your current membership period.
                  </p>
                  
                  <div className="payment-info-box">
                    <h3>Payment Information</h3>
                    {localCardDetails ? (
                      <div className="saved-card-info">
                        <div className="saved-card-option">
                          <input
                            type="checkbox"
                            id="useSavedCard"
                            checked={useSavedCard}
                            onChange={(e) => setUseSavedCard(e.target.checked)}
                            className="saved-card-checkbox"
                          />
                          <label htmlFor="useSavedCard" className="saved-card-label">
                            Use saved card for automatic payments
                          </label>
                        </div>
                        <div className="card-details">
                          <span className="card-brand">{localCardDetails.brand.charAt(0).toUpperCase() + localCardDetails.brand.slice(1)}</span>
                          <span className="card-last4">•••• {localCardDetails.last4}</span>
                          <span className="card-expiry">Expires {localCardDetails.exp_month}/{localCardDetails.exp_year}</span>
                        </div>
                      </div>
                    ) : (
                      <p>You'll need to add a payment method for automatic renewals.</p>
                    )}
                    <p className="next-payment">
                      First recurring payment date: <strong>{calculateNextPaymentDate()}</strong>
                    </p>
                    {/* <p className="payment-note">
                      Note: Your current membership will continue until {calculateNextPaymentDate()}. 
                      The recurring payments will begin after that date.
                    </p> */}
                  </div>
                </div>
                
                <div className="modal-buttons">
                  {localCardDetails ? (
                    <>
                      <button 
                        className="makeRecurringMembershipModalConfirmButton" 
                        onClick={() => handleMakeRecurring()}
                        disabled={isSaving}
                        aria-label="Confirm recurring membership"
                      >
                        {isSaving ? 'Processing...' : 'Yes, Make it Recurring'}
                      </button>
                      {!useSavedCard && (
                        <button 
                          className="makeRecurringMembershipModalSecondaryButton" 
                          onClick={() => setShowCardForm(true)}
                          disabled={isSaving}
                          aria-label="Add new payment method"
                        >
                          Add New Payment Method
                        </button>
                      )}
                    </>
                  ) : (
                    <button 
                      className="makeRecurringMembershipModalConfirmButton" 
                      onClick={() => setShowCardForm(true)}
                      aria-label="Add payment method"
                    >
                      Add Payment Method
                    </button>
                  )}
                  <button 
                    className="makeRecurringMembershipModalCancelButton" 
                    onClick={onCancel}
                    disabled={isSaving}
                    aria-label="Cancel recurring membership"
                  >
                    Cancel
                  </button>
                </div>
                {errorMessage && (
                  <div className="error-message" role="alert">
                    {errorMessage}
                  </div>
                )}
              </>
            ) : (
              <div className="card-form-section">
                <h3>Add New Payment Method</h3>
                <p>Enter your card details below. This will replace your current saved card (if present) and be used for automatic monthly renewals.</p>
                
                <form onSubmit={handleSaveCard} className="card-form">
                  <div className="card-element-wrapper">
                    <CardElement 
                      options={{
                        style: {
                          base: {
                            fontSize: '16px',
                            color: '#2c3e50',
                            '::placeholder': {
                              color: '#aab7c4'
                            }
                          },
                          invalid: {
                            color: '#e74c3c'
                          }
                        }
                      }}
                    />
                  </div>

                  {errorMessage && (
                    <div className="error-message" role="alert">
                      {errorMessage}
                    </div>
                  )}

                  <div className="modal-buttons">
                    <button
                      className="makeRecurringMembershipModalConfirmButton"
                      type="submit"
                      disabled={isSaving || !stripe || !elements}
                      aria-busy={isSaving}
                    >
                      {isSaving ? 'Processing...' : 'Save Card & Make Recurring'}
                    </button>
                    <button
                      type="button"
                      className="makeRecurringMembershipModalCancelButton"
                      onClick={() => setShowCardForm(false)}
                      disabled={isSaving}
                    >
                      Back
                    </button>
                  </div>
                </form>
              </div>
            )}
          </div>
        </div>
      )}

      {showSuccessModal && (
        <div 
          className="overlay success-overlay" 
          onClick={handleSuccessModalClose}
          role="dialog"
          aria-modal="true"
          aria-labelledby="success-modal-title"
        >
          <div 
            className="success-modal" 
            onClick={e => e.stopPropagation()}
            tabIndex="-1"
          >
            <button 
              className="success-modal-close-button" 
              onClick={handleSuccessModalClose}
              aria-label="Close success modal"
            >
              ×
            </button>
            
            <h2 id="success-modal-title">Success!</h2>
            <p>Your membership has been successfully set to recurring.</p>
            <p>Your next payment will be processed on {calculateNextPaymentDate()}.</p>
            
            <button 
              className="success-modal-button" 
              onClick={handleSuccessModalClose}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </>
  );
}

export default MakeRecurringMembershipModal;
